/**
 * PDF Renderer — ortak PDF üretim helper'ları.
 *
 * Yeni doküman component'lerinin (Kommissionierschein, Lieferschein,
 * Rechnung) paylaştığı altyapı:
 *   - fetchPdfSettings()          pdf_settings tablosundan ilk kaydı alır
 *   - downloadPdfFromHtml(html,…) html2pdf ile indirir (Safari/iOS
 *                                  için pop-up + print fallback)
 *   - renderCompanyHeader(settings)   ortak header HTML bloğu
 *   - formatCurrency / formatDate      biçimlendirme yardımcıları
 *
 * Mevcut OrderPDFGenerator.jsx hala kendi private fonksiyonlarını
 * kullanıyor; bu modül onun mantığını yeniden yazmak yerine üç yeni
 * doküman tipinde paylaşıyor.
 */
import { base44 } from '@/api/base44Client';

// Kurumsal kimlik renkleri — Angebot/Bestellung referans şablonuyla aynı
export const PDF_COLORS = {
  navy: '#172554',
  gold: '#D4A017',
  text: '#1e293b',
  gray: '#6b7280',
  lightGray: '#f3f4f6',
  border: '#d1d5db',
};

// pdf_settings boş/eksikse kullanılan varsayılanlar (OrderPDFGenerator ile aynı)
export const PDF_DEFAULTS = {
  header_logo_url: '',
  header_left_title: 'I M INMOTION',
  header_left_tagline: 'IMMER IN BEWEGUNG',
  header_right_title: 'GROSSHANDEL | HORECA',
  header_right_subtitle: 'VERPACKUNG | HYGIENE | REINIGUNGSMITTEL',
  company_name: 'InMotion Großhandel UG',
  street: 'Asbach 19',
  zip: '86510',
  city: 'Ried',
  country: 'Deutschland',
  phone: '089 357 650 - 41',
  email: 'info@inmotion-handel.de',
  website: 'www.inmotion-handel.de',
  registry_city: 'Augsburg',
  hr_nr: 'HRB40485',
  steuer_nr: '102/129/30143',
  geschaeftsfuehrer: 'Bünyamin Cicekdag',
  bank_name: 'DEUTSCHE BANK',
  iban: 'DE13 7207 0001 0101 2160 00',
  bic: 'DEUTDEMM720',
  ansprechpartner: 'Buchhaltung',
};

let settingsCache = null;

/**
 * Wählt aus allen pdf_settings-Zeilen deterministisch die ÄLTESTE (erste
 * angelegte). Doppelte Zeilen (entstanden durch wiederholtes Anlegen)
 * werden nie gelöscht, nur gemeldet – alle Dokumente und die Admin-Seite
 * lesen dadurch dieselbe Zeile.
 * @param {Array<object>|undefined} rows
 * @returns {{ row: object|null, duplicates: number }}
 */
export function pickPdfSettingsRow(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  if (list.length === 0) return { row: null, duplicates: 0 };
  const ts = (r) => {
    const t = Date.parse(r.created_date || r.created_at || '');
    return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
  };
  const sorted = [...list].sort((a, b) => ts(a) - ts(b) || String(a.id).localeCompare(String(b.id)));
  return { row: sorted[0], duplicates: sorted.length - 1 };
}

/** Rohzeile aus pdf_settings (ungemischt) + Anzahl Duplikate. */
export async function loadPdfSettingsRow() {
  const list = await base44.entities.PdfSetting.list('created_date');
  return pickPdfSettingsRow(list);
}

export async function fetchPdfSettings() {
  if (settingsCache) return settingsCache;
  let row = {};
  try {
    row = (await loadPdfSettingsRow()).row || {};
  } catch (err) {
    console.warn('[pdfRenderer] pdf_settings fetch failed, using defaults', err);
  }
  // Boş alanlar varsayılana düşer — tüm belgeler eksiksiz kimlik taşır
  const merged = { ...PDF_DEFAULTS };
  Object.keys(row).forEach((key) => {
    if (row[key] != null && row[key] !== '') merged[key] = row[key];
  });
  settingsCache = merged;
  return settingsCache;
}

export function clearPdfSettingsCache() {
  settingsCache = null;
}

export function formatCurrency(value, currency = 'EUR') {
  const n = Number(value) || 0;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(n);
}

export function formatDate(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Tüm doküman template'lerinin üst kısmındaki ortak marka bloğu.
 * pdf_settings boşsa makul varsayılanları yazar.
 */
export function renderCompanyHeader(settings = {}) {
  const s = { ...PDF_DEFAULTS, ...(settings || {}) };
  const C = PDF_COLORS;

  const logoHtml = s.header_logo_url
    ? `<img src="${escapeHtml(s.header_logo_url)}" alt="Logo" style="max-height:55px;max-width:180px;object-fit:contain;">`
    : `<div>
        <div style="font-size:30px;font-weight:900;letter-spacing:-2px;line-height:1;color:white;">
          I M<span style="display:inline-block;width:6px;height:6px;background:${C.gold};border-radius:50%;vertical-align:super;margin-left:2px;"></span>
        </div>
        <div style="font-size:7px;letter-spacing:3px;text-transform:uppercase;color:rgba(255,255,255,0.8);margin-top:2px;">${escapeHtml(s.header_left_title || '')}</div>
        <div style="font-size:10px;font-weight:800;letter-spacing:2px;text-transform:uppercase;color:white;margin-top:3px;">${escapeHtml(s.header_left_tagline || '')}</div>
      </div>`;

  // Kurumsal lacivert banner — Angebot/Bestellung referans şablonuyla aynı.
  // Negatif margin: wrapPdfDocument'in .page padding'ini aşıp tam genişliğe yayılır.
  return `
    <div style="background:${C.navy};color:white;padding:10px 30px;display:flex;justify-content:space-between;align-items:center;margin:-28px -30px 12px -30px;">
      <div style="display:flex;align-items:center;gap:10px;">${logoHtml}</div>
      <div style="text-align:right;">
        <div style="font-size:14px;font-weight:800;letter-spacing:2px;color:${C.gold};">${escapeHtml(s.header_right_title || '')}</div>
        <div style="font-size:6.5px;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,0.7);margin-top:2px;">${escapeHtml(s.header_right_subtitle || '')}</div>
      </div>
    </div>
    <div style="font-size:7pt;color:${C.gray};text-decoration:underline;margin:0 0 10px;">
      ${escapeHtml([s.company_name, s.street, [s.zip, s.city].filter(Boolean).join(' ')].filter(Boolean).join(' · '))}
    </div>
  `;
}

export function renderFooter(settings = {}) {
  const s = { ...PDF_DEFAULTS, ...(settings || {}) };
  const C = PDF_COLORS;
  // 4 sütunlu kurumsal footer — referans şablonla aynı içerik ve düzen
  return `
    <div class="pdf-footer" style="margin-top:24px;border-top:2px solid ${C.navy};padding:6px 0 0;display:flex;justify-content:space-between;font-size:6.5pt;color:${C.gray};line-height:1.4;">
      <div style="flex:1;">${escapeHtml(s.company_name)}<br>${escapeHtml(s.street)}<br>${escapeHtml(`${s.zip} ${s.city}`)}<br>${escapeHtml(s.country)}</div>
      <div style="flex:1;">Tel.: ${escapeHtml(s.phone)}<br>E-Mail: ${escapeHtml(s.email)}<br>Web: ${escapeHtml(s.website)}</div>
      <div style="flex:1;">${escapeHtml(s.registry_city)}<br>HR-Nr.: ${escapeHtml(s.hr_nr)}<br>Steuer-Nr.: ${escapeHtml(s.steuer_nr)}<br>Geschäftsführung: ${escapeHtml(s.geschaeftsfuehrer)}</div>
      <div style="flex:1;text-align:right;">${escapeHtml(s.bank_name)}<br>IBAN: ${escapeHtml(s.iban)}<br>BIC: ${escapeHtml(s.bic)}</div>
    </div>
  `;
}

export function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Gövde HTML'ini tam bir PDF dokümanına sarar. Belge şablonları yalnızca
 * gövde üretir; tekil indirmeler bu wrapper'ı kullanır, çok sayfalı
 * paketler gövdeleri `page-break-before` div'leriyle birleştirir.
 */
export function wrapPdfDocument(bodyHtml, title = '') {
  const C = PDF_COLORS;
  // Tam A4 kurumsal sayfa: 210 mm genişlik, ekranda gölgeli önizleme,
  // baskıda kenarlıksız. Çok sayfalı paketler page-break-before ile
  // aynı .page kabı içinde akar (her gövde kendi header/footer'ını taşır).
  return `
    <!DOCTYPE html>
    <html lang="de">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=794">
      <title>${escapeHtml(title)}</title>
      <style>
        @page { size: A4; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: ${C.text}; font-size: 10pt; line-height: 1.35; background: #fff; }
        .page { width: 210mm; min-height: 297mm; margin: 0; padding: 28px 30px 30px; position: relative; background: #fff; display: flex; flex-direction: column; }
        .pdf-footer { margin-top: auto; }
        @media screen { body { background: #f1f5f9; } .page { margin: 0 auto; box-shadow: 0 2px 12px rgba(0,0,0,0.15); } }
        @media print { .page { margin: 0; box-shadow: none; } }
        table { border-collapse: collapse; }
        /* Seitenumbruch: Zeilen nie zerschneiden, Tabellenkopf je Seite wiederholen */
        tr { page-break-inside: avoid; break-inside: avoid; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }
        .avoid-break { page-break-inside: avoid; break-inside: avoid; }
      </style>
    </head>
    <body>
      <div class="page">
        ${bodyHtml}
      </div>
    </body>
    </html>
  `;
}

// html2canvas liefert bei sehr hohen Canvases leere Seiten (Browser-Limit
// ~16384 px). Skalierung wird aus der gemessenen Dokumenthöhe abgeleitet.
const CANVAS_MAX_PX = 16000;
const CANVAS_PREFERRED_SCALE = 2;

/**
 * Skalierungsfaktor für html2canvas: so hoch wie möglich (scharfe Schrift),
 * aber nie über dem Canvas-Limit.
 * @param {number} contentHeightPx gemessene Höhe des Dokuments bei scale 1
 */
export function pdfCanvasScale(contentHeightPx) {
  const h = Number(contentHeightPx);
  if (!Number.isFinite(h) || h <= 0) return CANVAS_PREFERRED_SCALE;
  const fit = Math.floor((CANVAS_MAX_PX / h) * 100) / 100;
  return Math.max(1, Math.min(CANVAS_PREFERRED_SCALE, fit));
}

/**
 * html2canvas rendert die BILDSCHIRM-Ansicht: Seitenschatten, grauer
 * Hintergrund, min-height 297 mm. Ein Pixel Überhang erzeugt dann eine leere
 * zweite Seite. Für die Canvas-Variante werden diese Regeln neutralisiert.
 */
export function prepareHtmlForCanvas(html) {
  const style = '<style data-pdf-canvas>html,body{background:#fff!important}'
    + '.page{min-height:0!important;box-shadow:none!important;margin:0!important}</style>';
  const s = String(html || '');
  return s.includes('</head>') ? s.replace('</head>', `${style}</head>`) : style + s;
}

/** Misst die Dokumenthöhe in einem unsichtbaren iframe (Breite = A4 bei 96 dpi). */
async function measureHtmlHeight(html) {
  if (typeof document === 'undefined') return 0;
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;height:1123px;visibility:hidden;border:0';
  document.body.appendChild(frame);
  try {
    const doc = frame.contentDocument;
    doc.open();
    doc.write(html);
    doc.close();
    await new Promise((res) => setTimeout(res, 50));
    return doc.documentElement?.scrollHeight || doc.body?.scrollHeight || 0;
  } catch {
    return 0;
  } finally {
    frame.remove();
  }
}

/**
 * HTML içeriği PDF olarak indirir. Safari/iOS için window.open + print
 * (html2canvas CORS hatalarından kaçınmak için), diğer tarayıcılar
 * için html2pdf kullanır.
 *
 * @param {string} htmlContent - tam bir HTML sayfası (<html>…)
 * @param {string} fileName    - uzantısız dosya adı (PDF için .pdf eklenir)
 */
export async function downloadPdfFromHtml(htmlContent, fileName) {
  const ua = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // MOBİLDE her zaman yazdırma penceresi kullanılır: html2canvas mobil
  // tarayıcılarda canvas boyut limitleri yüzünden BOŞ PDF üretebiliyor
  // (özellikle çok sayfalı paketlerde). Yerel "PDF olarak kaydet" akışı
  // her cihazda güvenilir ve içerik tam gelir.
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const isApple = isSafari || isIOS || isMobile;

  let pdfWindow = null;
  if (isApple) {
    pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.document.write(
        '<html><body style="font-family:sans-serif;text-align:center;padding-top:20vh;color:#333;">' +
        'Lade Ansicht... Bitte warten.</body></html>'
      );
    }
  }

  try {
    if (isApple) {
      if (pdfWindow) {
        pdfWindow.document.open();
        pdfWindow.document.write(htmlContent);
        pdfWindow.document.close();
        pdfWindow.document.title = fileName;
        pdfWindow.focus();
        // Görseller yüklenmeden yazdırma tetiklenirse sayfalar eksik/boş
        // çıkar — tüm <img>'lerin bitmesini bekle (üst sınır 4 sn).
        const waitImages = () => {
          const imgs = Array.from(pdfWindow.document.images || []);
          const pending = imgs.filter((img) => !img.complete);
          if (pending.length === 0) return Promise.resolve();
          return Promise.race([
            Promise.all(pending.map((img) => new Promise((res) => {
              img.onload = img.onerror = res;
            }))),
            new Promise((res) => setTimeout(res, 4000)),
          ]);
        };
        waitImages().then(() => setTimeout(() => pdfWindow.print(), 300));
      } else {
        alert('Pop-up Blocker aktiv. Bitte erlauben Sie Pop-ups für diese Seite.');
      }
      return;
    }

    if (typeof window !== 'undefined' && window.html2pdf) {
      const canvasHtml = prepareHtmlForCanvas(htmlContent);
      const scale = pdfCanvasScale(await measureHtmlHeight(canvasHtml));
      const opt = {
        margin: 0,
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale, useCORS: true, allowTaint: true, logging: false, imageTimeout: 5000 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        // CSS-Umbrüche respektieren, Tabellenzeilen nie zerschneiden
        pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', '.avoid-break'] },
      };
      await window.html2pdf().set(opt).from(canvasHtml).save();
      return;
    }

    throw new Error('html2pdf.js Bibliothek fehlt');
  } catch (err) {
    console.error('[pdfRenderer] PDF Generation error:', err);
    if (pdfWindow) pdfWindow.close();
    throw err;
  }
}

/**
 * Sipariş pozisyonlarını lagerort'a göre doğal sıralar. Aynı rafta
 * olan ürünler birlikte toplanır; lagerort yoksa sona eklenir.
 */
export function sortItemsByLagerort(items, products = []) {
  const productMap = new Map(products.map(p => [p.id, p]));
  const enriched = items.map((it) => {
    const prod = productMap.get(it.product_id);
    return { ...it, _lagerort: (prod?.lagerort || '').trim() };
  });
  return enriched.sort((a, b) => {
    // Lagerort'u olmayanları sona at.
    if (!a._lagerort && !b._lagerort) return 0;
    if (!a._lagerort) return 1;
    if (!b._lagerort) return -1;
    return a._lagerort.localeCompare(b._lagerort, 'de', { numeric: true });
  });
}
